import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Plus, Trash2, Edit2, Eye, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import { API_BASE } from '../../utils/api-base';

type DataSource = 'plc' | 'energy_meter' | 'scanner' | 'static';
type WidgetType = 'value' | 'chart' | 'gauge' | 'table' | 'shortcut';
type ChartType = 'line' | 'area' | 'bar' | 'pie' | 'gauge' | 'sparkline';

interface DashboardWidget {
  id: string;
  clientId: string;
  title: string;
  widgetType: WidgetType;
  dataSource: DataSource;
  deviceId: string;
  tagId: string;
  chartType: ChartType;
  unit?: string;
  color?: string;
  width?: 'small' | 'medium' | 'large' | 'full';
  position?: number;
  isVisible?: boolean;
  config?: any;
}

const defaultForm = {
  title: '',
  widgetType: 'chart' as WidgetType,
  dataSource: 'plc' as DataSource,
  deviceId: '',
  tagId: '',
  chartType: 'line' as ChartType,
  unit: '',
  color: '#3b82f6',
  width: 'medium' as 'small' | 'medium' | 'large' | 'full',
  position: 0,
  isVisible: true,
  points: 60,
};

const previewChartData = [
  { time: 'T-50', value: 0 },
  { time: 'T-40', value: 0 },
  { time: 'T-30', value: 0 },
  { time: 'T-20', value: 0 },
  { time: 'T-10', value: 0 },
  { time: 'Now', value: 0 },
];

const previewGaugeData = [{ name: 'Value', value: 0 }, { name: 'Remaining', value: 100 }];

export function WidgetsTab() {
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(defaultForm);
  const session = sessionStorage.get();
  const token = session?.token || '';

  const selectedDevice = useMemo(
    () => devices.find((device) => device.id === formData.deviceId),
    [devices, formData.deviceId]
  );

  useEffect(() => {
    const loadClients = async () => {
      if (!token) return;
      try {
        const data = await adminApi.getClients(token);
        setClients(data || []);
        if (data?.length && !selectedClient) {
          setSelectedClient(data[0].id);
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to load clients');
      }
    };

    loadClients();
  }, [token]);

  useEffect(() => {
    if (!token || !selectedClient) return;
    loadWidgets(selectedClient);
  }, [token, selectedClient]);

  useEffect(() => {
    if (!token || !selectedClient) return;
    loadDevices();
  }, [token, selectedClient, formData.dataSource]);

  useEffect(() => {
    if (!token || !selectedClient || !formData.deviceId) {
      setTags([]);
      return;
    }
    loadTags();
  }, [token, selectedClient, formData.dataSource, formData.deviceId]);

  const loadWidgets = async (clientId = selectedClient) => {
    try {
      setLoading(true);
      const data = await adminApi.getDashboardWidgets(token, clientId, true);
      setWidgets(data || []);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load widgets');
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      setDevices([]);
      setTags([]);
      setFormData((current) => ({ ...current, deviceId: '', tagId: '' }));

      if (formData.dataSource === 'plc') {
        setDevices(await adminApi.getPLCs(token, selectedClient));
      } else if (formData.dataSource === 'energy_meter') {
        setDevices(await adminApi.getEnergyMeters(token, selectedClient));
      } else if (formData.dataSource === 'scanner') {
        const response = await fetch(`${API_BASE}/api/clients/${selectedClient}/scanners`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.error || 'Failed to load scanners');
        setDevices(payload.scanners || []);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load devices');
    }
  };

  const loadTags = async () => {
    try {
      if (formData.dataSource === 'plc') {
        setTags(await adminApi.getTags(token, selectedClient, formData.deviceId));
      } else if (formData.dataSource === 'energy_meter') {
        setTags(await adminApi.getEnergyMeterTags(token, selectedClient, formData.deviceId));
      } else if (formData.dataSource === 'scanner') {
        const scanner = devices.find((device) => device.id === formData.deviceId);
        setTags(scanner?.tags || []);
      } else {
        setTags([]);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load tags');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData(defaultForm);
    setShowForm(false);
  };

  const startCreate = () => {
    setEditingId(null);
    setFormData(defaultForm);
    setShowForm(true);
  };

  const startEdit = (widget: DashboardWidget) => {
    setEditingId(widget.id);
    setFormData({
      title: widget.title || '',
      widgetType: widget.widgetType || 'chart',
      dataSource: widget.dataSource || 'plc',
      deviceId: widget.deviceId || '',
      tagId: widget.tagId || '',
      chartType: widget.chartType || 'line',
      unit: widget.unit || '',
      color: widget.color || '#3b82f6',
      width: (widget.width as any) || 'medium',
      position: Number(widget.position || 0),
      isVisible: widget.isVisible !== false,
      points: Number(widget.config?.points || 60),
    });
    setShowForm(true);
  };

  const saveWidget = async () => {
    if (!selectedClient) {
      toast.error('Select a client first');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Widget title is required');
      return;
    }
    if (formData.dataSource !== 'static' && (!formData.deviceId || !formData.tagId)) {
      toast.error('Select a device and tag');
      return;
    }

    const selectedTag = tags.find((tag) => tag.id === formData.tagId);
    const payload = {
      ...formData,
      config: {
        points: formData.points,
        deviceName: selectedDevice?.name || '',
        tagName: selectedTag?.name || selectedTag?.tagName || '',
      },
    };

    try {
      if (editingId) {
        await adminApi.updateDashboardWidget(token, selectedClient, editingId, payload);
        toast.success('Widget updated');
      } else {
        await adminApi.createDashboardWidget(token, selectedClient, payload);
        toast.success('Widget created');
      }
      resetForm();
      await loadWidgets(selectedClient);
    } catch (error: any) {
      toast.error(error.message || 'Failed to save widget');
    }
  };

  const deleteWidget = async (widget: DashboardWidget) => {
    if (!confirm(`Delete "${widget.title}"?`)) return;
    try {
      await adminApi.deleteDashboardWidget(token, selectedClient, widget.id);
      toast.success('Widget deleted');
      await loadWidgets(selectedClient);
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete widget');
    }
  };

  const renderPreviewChart = (type: ChartType, color = formData.color) => {
    if (type === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={90}>
          <BarChart data={previewChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (type === 'area') {
      return (
        <ResponsiveContainer width="100%" height={90}>
          <AreaChart data={previewChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <Area type="monotone" dataKey="value" stroke={color} fill={color} fillOpacity={0.25} />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    if (type === 'pie' || type === 'gauge') {
      return (
        <ResponsiveContainer width="100%" height={90}>
          <PieChart>
            <Pie
              data={previewGaugeData}
              dataKey="value"
              innerRadius={type === 'gauge' ? 26 : 18}
              outerRadius={type === 'gauge' ? 36 : 40}
              startAngle={type === 'gauge' ? 180 : 0}
              endAngle={type === 'gauge' ? 0 : 360}
              paddingAngle={2}
            >
              <Cell fill={color} />
              <Cell fill="#1f2937" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (type === 'sparkline') {
      return (
        <ResponsiveContainer width="100%" height={50}>
          <LineChart data={previewChartData}>
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={90}>
        <LineChart data={previewChartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard Widgets</h2>
          <p className="text-sm sm:text-base text-muted-foreground">Create persistent client dashboard widgets from PLC, energy meter, and scanner tags</p>
        </div>
        <Button onClick={startCreate} className="gap-2">
          <Plus className="h-4 w-4" /> Create Widget
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid gap-3 sm:grid-cols-[minmax(220px,360px)_1fr] sm:items-end">
            <div className="space-y-2">
              <Label>Client</Label>
              <select
                value={selectedClient}
                onChange={(event) => setSelectedClient(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Select client...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>
            <p className="text-sm text-muted-foreground">
              Widgets saved here are available to the selected client from any browser or device.
            </p>
          </div>
        </CardContent>
      </Card>

      {showForm && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader>
              <CardTitle>{editingId ? 'Edit Widget' : 'Create Widget'}</CardTitle>
              <CardDescription>Choose what the client should see on their dashboard</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Boiler Temperature" />
                </div>
                <div className="space-y-2">
                  <Label>Widget Type</Label>
                  <select value={formData.widgetType} onChange={(e) => setFormData({ ...formData, widgetType: e.target.value as WidgetType })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="value">Live Value Card</option>
                    <option value="chart">Chart</option>
                    <option value="gauge">Gauge</option>
                    <option value="table">Mini Table</option>
                    <option value="shortcut">Report Shortcut</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Data Source</Label>
                  <select value={formData.dataSource} onChange={(e) => setFormData({ ...formData, dataSource: e.target.value as DataSource })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="plc">PLC Tag</option>
                    <option value="energy_meter">Energy Meter Tag</option>
                    <option value="scanner">Scanner Tag</option>
                    <option value="static">Static / Shortcut</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Device</Label>
                  <select value={formData.deviceId} disabled={formData.dataSource === 'static'} onChange={(e) => setFormData({ ...formData, deviceId: e.target.value, tagId: '' })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50">
                    <option value="">Select device...</option>
                    {devices.map((device) => <option key={device.id} value={device.id}>{device.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Tag / Parameter</Label>
                  <select value={formData.tagId} disabled={formData.dataSource === 'static' || !formData.deviceId} onChange={(e) => setFormData({ ...formData, tagId: e.target.value })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50">
                    <option value="">Select tag...</option>
                    {tags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}{tag.unit ? ` (${tag.unit})` : ''}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Chart Type</Label>
                  <select value={formData.chartType} onChange={(e) => setFormData({ ...formData, chartType: e.target.value as ChartType })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="line">Line</option>
                    <option value="area">Area</option>
                    <option value="bar">Bar</option>
                    <option value="pie">Pie / Donut</option>
                    <option value="gauge">Gauge</option>
                    <option value="sparkline">Sparkline</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <Input type="color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Width</Label>
                  <select value={formData.width} onChange={(e) => setFormData({ ...formData, width: e.target.value as any })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="full">Full Row</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Trend Points</Label>
                  <Input type="number" min={1} max={500} value={formData.points} onChange={(e) => setFormData({ ...formData, points: Number(e.target.value) || 60 })} />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox id="widget-visible" checked={formData.isVisible} onCheckedChange={(checked) => setFormData({ ...formData, isVisible: checked === true })} />
                <Label htmlFor="widget-visible">Visible to client</Label>
              </div>

              <div className="rounded-lg border bg-slate-950 p-4 text-white">
                <p className="mb-3 text-xs font-medium text-slate-400">Preview</p>
                <div className="rounded-md bg-slate-900 p-4">
                  <div className="mb-3 flex items-start justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase" style={{ color: formData.color }}>{formData.title || 'Widget Title'}</p>
                      <p className="text-xs text-slate-400">{selectedDevice?.name || 'Selected device'}</p>
                    </div>
                    <p className="text-2xl font-bold">{formData.unit ? `-- ${formData.unit}` : '--'}</p>
                  </div>
                  {renderPreviewChart(formData.widgetType === 'gauge' ? 'gauge' : formData.chartType)}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Button onClick={saveWidget} className="gap-2">
                  <Save className="h-4 w-4" /> {editingId ? 'Update Widget' : 'Save Widget'}
                </Button>
                <Button variant="outline" onClick={resetForm} className="gap-2">
                  <X className="h-4 w-4" /> Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {loading ? (
          <Card className="p-8 text-center md:col-span-2 xl:col-span-3">Loading widgets...</Card>
        ) : widgets.length === 0 ? (
          <Card className="p-8 text-center md:col-span-2 xl:col-span-3">
            <p className="text-muted-foreground">No widgets for this client yet.</p>
          </Card>
        ) : (
          widgets.map((widget, index) => (
            <motion.div key={widget.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}>
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-lg">{widget.title}</CardTitle>
                      <CardDescription>{widget.config?.deviceName || widget.dataSource} • {widget.config?.tagName || widget.tagId || 'No tag'}</CardDescription>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant="outline">{widget.widgetType}</Badge>
                      <Badge variant={widget.isVisible === false ? 'secondary' : 'default'}>{widget.isVisible === false ? 'Hidden' : 'Visible'}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="h-24 rounded bg-muted/40 p-2">
                    {renderPreviewChart(widget.widgetType === 'gauge' ? 'gauge' : widget.chartType, widget.color || '#3b82f6')}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => startEdit(widget)} className="flex-1 gap-1">
                      <Edit2 className="h-3 w-3" /> Edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteWidget(widget)} className="flex-1 gap-1">
                      <Trash2 className="h-3 w-3" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="h-4 w-4" /> How Widgets Work
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Create widgets from a PLC tag, energy meter tag, or scanner tag. The client dashboard loads these saved widgets from the database and updates from historical/live readings.
        </CardContent>
      </Card>
    </div>
  );
}
